/**
 * Sklep: filtry (AJAX + historia przeglądarki), szuflada na mobile,
 * suwak ceny, wyszukiwarka w długich listach atrybutów.
 * Bez JS działa jako zwykły formularz GET.
 */

const DESKTOP = window.matchMedia('(min-width: 1024px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

const normalize = (str) =>
	String(str)
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/ł/g, 'l');

export const initShop = () => {
	const shop = document.querySelector('.js-shop');
	if (!shop) return;

	const form = shop.querySelector('.js-shop-form');
	const panel = document.getElementById('shop-filters');
	const backdrop = document.querySelector('.js-shop-backdrop');
	const announcer = document.querySelector('.js-shop-announcer');

	let controller = null;
	let lastFocus = null;

	const getResults = () => document.querySelector('.js-shop-results');

	/* ------------------------------------------------------------------
	 * Suwak ceny
	 * --------------------------------------------------------------- */
	const setPrice = (wrap, minValue, maxValue) => {
		const bounds = { min: Number(wrap.dataset.min), max: Number(wrap.dataset.max) };
		const min = Math.max(bounds.min, Math.min(minValue, maxValue));
		const max = Math.min(bounds.max, Math.max(maxValue, min));
		const span = bounds.max - bounds.min || 1;

		const ranges = wrap.querySelectorAll('.js-price-range');
		const inputs = wrap.querySelectorAll('.js-price-input');

		ranges[0].value = min;
		ranges[1].value = max;
		inputs[0].value = min;
		inputs[1].value = max;

		// Gdy uchwyty stykają się przy prawej krawędzi – "min" na wierzch
		ranges[0].style.zIndex = min >= bounds.max - span * 0.05 ? '5' : '';

		const track = wrap.querySelector('.js-price-track');
		track.style.left = ((min - bounds.min) / span) * 100 + '%';
		track.style.right = ((bounds.max - max) / span) * 100 + '%';
	};

	/* ------------------------------------------------------------------
	 * URL z formularza (czyste adresy: filter_autor=a,b)
	 * --------------------------------------------------------------- */
	const buildUrl = () => {
		const url = new URL(form.action);
		const grouped = {};

		new FormData(form).forEach((value, key) => {
			if (value === '') return;
			const name = key.replace(/\[\]$/, '');
			if (!grouped[name]) grouped[name] = [];
			grouped[name].push(value);
		});

		const price = form.querySelector('.js-price');
		if (price) {
			if (grouped.min_price && Number(grouped.min_price[0]) <= Number(price.dataset.min)) delete grouped.min_price;
			if (grouped.max_price && Number(grouped.max_price[0]) >= Number(price.dataset.max)) delete grouped.max_price;
		}

		const sort = document.querySelector('.js-shop-sort');
		if (sort && grouped.orderby && grouped.orderby[0] === sort.dataset.default) delete grouped.orderby;

		Object.keys(grouped).forEach((name) => {
			url.searchParams.set(name, grouped[name].join(','));
		});

		return url.toString();
	};

	/* ------------------------------------------------------------------
	 * Formularz ← URL (wstecz/dalej, chipsy, "wyczyść")
	 * --------------------------------------------------------------- */
	const syncForm = (href) => {
		const params = new URL(href, window.location.origin).searchParams;

		form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
			const name = input.name.replace(/\[\]$/, '');
			const values = (params.get(name) || '').split(',');
			input.checked = values.indexOf(input.value) !== -1;
		});

		const price = form.querySelector('.js-price');
		if (price) {
			setPrice(
				price,
				Number(params.get('min_price') || price.dataset.min),
				Number(params.get('max_price') || price.dataset.max)
			);
		}
	};

	/* ------------------------------------------------------------------
	 * Pobieranie wyników
	 * --------------------------------------------------------------- */
	const setLoading = (on) => {
		const results = getResults();
		if (!results) return;
		results.setAttribute('aria-busy', on ? 'true' : 'false');
		results.classList.toggle('is-loading', on);
	};

	const afterUpdate = (results) => {
		const total = results.dataset.total || '0';

		document.querySelectorAll('.js-shop-total').forEach((el) => {
			el.textContent = total;
		});

		if (announcer) {
			announcer.textContent = total === '0' ? 'Brak pasujących produktów' : results.dataset.resultText;
		}
	};

	const load = (url, options) => {
		const opts = Object.assign({ push: true, scroll: false }, options);

		if (controller) controller.abort();
		const current = new AbortController();
		controller = current;

		setLoading(true);

		return fetch(url, {
			headers: { 'X-Shop-Partial': '1' },
			credentials: 'same-origin',
			signal: current.signal,
		})
			.then((res) => {
				if (!res.ok) throw new Error(String(res.status));
				return res.text();
			})
			.then((html) => {
				const doc = new DOMParser().parseFromString(html, 'text/html');
				const fresh = doc.querySelector('.js-shop-results');
				const old = getResults();

				if (!fresh || !old) {
					window.location.href = url;
					return;
				}

				old.replaceWith(fresh);
				if (opts.push) window.history.pushState({ shop: true }, '', url);
				afterUpdate(fresh);

				if (opts.scroll) {
					fresh.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'start' });
				}
			})
			.catch((err) => {
				if (err.name === 'AbortError') return;
				window.location.href = url; // awaryjnie: zwykłe przejście
			})
			.finally(() => {
				if (controller === current) setLoading(false);
			});
	};

	/* ------------------------------------------------------------------
	 * Szuflada filtrów (mobile)
	 * --------------------------------------------------------------- */
	const isOpen = () => panel.classList.contains('is-open');

	const setExpanded = (value) => {
		document.querySelectorAll('.js-shop-open').forEach((btn) => btn.setAttribute('aria-expanded', value));
	};

	const openPanel = () => {
		lastFocus = document.activeElement;
		panel.inert = false;
		panel.classList.add('is-open');
		backdrop.classList.add('is-open');
		document.documentElement.style.overflow = 'hidden';
		setExpanded('true');
		panel.focus();
	};

	const closePanel = (restoreFocus) => {
		if (!isOpen()) return;
		panel.classList.remove('is-open');
		backdrop.classList.remove('is-open');
		document.documentElement.style.overflow = '';
		setExpanded('false');
		if (!DESKTOP.matches) panel.inert = true;

		if (restoreFocus !== false) {
			const target = lastFocus && document.contains(lastFocus) ? lastFocus : document.querySelector('.js-shop-open');
			if (target) target.focus();
		}
	};

	const syncPanelMode = () => {
		if (DESKTOP.matches) {
			closePanel(false);
			panel.inert = false;
			panel.removeAttribute('role');
			panel.removeAttribute('aria-modal');
		} else {
			panel.setAttribute('role', 'dialog');
			panel.setAttribute('aria-modal', 'true');
			panel.inert = !isOpen();
		}
	};

	syncPanelMode();
	DESKTOP.addEventListener('change', syncPanelMode);

	/* ------------------------------------------------------------------
	 * Zdarzenia (delegacja – działa po podmianie wyników)
	 * --------------------------------------------------------------- */
	document.addEventListener('change', (e) => {
		const el = e.target;

		// Suwak: pobieramy dopiero po puszczeniu uchwytu
		if (el.classList.contains('js-price-range')) {
			load(buildUrl());
			return;
		}

		if (!el.name || el.form !== form) return;

		if (el.classList.contains('js-price-input')) {
			const wrap = el.closest('.js-price');
			const inputs = wrap.querySelectorAll('.js-price-input');
			setPrice(wrap, Number(inputs[0].value), Number(inputs[1].value));
		}

		load(buildUrl());
	});

	document.addEventListener('input', (e) => {
		const el = e.target;

		// Suwak ceny – podgląd na żywo
		if (el.classList.contains('js-price-range')) {
			const wrap = el.closest('.js-price');
			const ranges = wrap.querySelectorAll('.js-price-range');
			let min = Number(ranges[0].value);
			let max = Number(ranges[1].value);
			if (min > max) {
				if (el === ranges[0]) min = max;
				else max = min;
			}
			setPrice(wrap, min, max);
			return;
		}

		// Wyszukiwarka w liście atrybutu
		if (el.classList.contains('js-filter-search')) {
			const group = el.closest('.js-filter-group');
			const query = normalize(el.value.trim());
			let visible = 0;

			group.querySelectorAll('.js-filter-item').forEach((item) => {
				const match = !query || normalize(item.dataset.name).indexOf(query) !== -1;
				item.hidden = !match;
				if (match) visible += 1;
			});

			const empty = group.querySelector('.js-filter-empty');
			if (empty) empty.classList.toggle('hidden', visible > 0);
		}
	});

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		load(buildUrl());
	});

	document.addEventListener('click', (e) => {
		const link = e.target.closest('.js-shop-results a.page-numbers, [data-shop-link]');
		if (link && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
			e.preventDefault();
			syncForm(link.href);
			load(link.href, { scroll: link.classList.contains('page-numbers') });
			return;
		}

		if (e.target.closest('.js-shop-open')) {
			openPanel();
			return;
		}

		if (e.target.closest('[data-shop-close]')) {
			closePanel();
		}
	});

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && isOpen()) closePanel();
	});

	// Fokus nie ucieka z otwartej szuflady
	document.addEventListener('focusin', (e) => {
		if (isOpen() && !panel.contains(e.target)) panel.focus();
	});

	window.addEventListener('popstate', () => {
		syncForm(window.location.href);
		load(window.location.href, { push: false });
	});
};
